import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Presentation, 
  Sparkles, 
  Plus, 
  ChevronLeft, 
  ChevronRight, 
  Edit2, 
  Trash2, 
  Download,
  RefreshCw,
  Layout,
  Image,
  FileText,
  Clock,
  Loader2,
  Copy,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

const LAYOUT_OPTIONS = [
  { value: 'title_only', label: 'Title Only', icon: Layout },
  { value: 'title_content', label: 'Title + Content', icon: FileText },
  { value: 'two_column', label: 'Two Columns', icon: Layout },
  { value: 'image_left', label: 'Image Left', icon: Image },
  { value: 'image_right', label: 'Image Right', icon: Image }
];

const STYLE_OPTIONS = [
  { value: 'professional', label: 'Professional', description: 'Clean, business-ready' },
  { value: 'creative', label: 'Creative', description: 'Bold, eye-catching' },
  { value: 'minimal', label: 'Minimal', description: 'Simple, focused' },
  { value: 'educational', label: 'Educational', description: 'Clear, instructional' }
];

export default function AIPresentationPage() {
  const { api, user } = useAuth();
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [presentations, setPresentations] = useState([]);
  const [currentPresentation, setCurrentPresentation] = useState(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [regenerateInstructions, setRegenerateInstructions] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const [newPresentation, setNewPresentation] = useState({
    topic: '',
    description: '',
    num_slides: 8,
    style: 'professional',
    audience: ''
  });

  useEffect(() => {
    checkAccess();
    fetchPresentations();
  }, []);

  const checkAccess = async () => {
    try {
      const response = await api.get('/presentations/check-access');
      setHasAccess(response.data.has_access);
    } catch (error) {
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchPresentations = async () => {
    try {
      const response = await api.get('/presentations/list');
      setPresentations(response.data.presentations || []);
    } catch (error) {
      console.error('Failed to fetch presentations:', error);
    }
  };

  const generatePresentation = async () => {
    if (!newPresentation.topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }

    setGenerating(true);
    try {
      const response = await api.post('/presentations/generate', newPresentation);
      toast.success('Presentation generated successfully!');
      setCurrentPresentation(response.data);
      setCurrentSlideIndex(0);
      setShowNewDialog(false);
      fetchPresentations();
      setNewPresentation({
        topic: '',
        description: '',
        num_slides: 8,
        style: 'professional',
        audience: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate presentation');
    } finally {
      setGenerating(false);
    }
  };

  const loadPresentation = async (id) => {
    try {
      const response = await api.get(`/presentations/${id}`);
      setCurrentPresentation(response.data);
      setCurrentSlideIndex(0);
    } catch (error) {
      toast.error('Failed to load presentation');
    }
  };

  const deletePresentation = async (id) => {
    if (!window.confirm('Delete this presentation?')) return;
    
    try {
      await api.delete(`/presentations/${id}`);
      toast.success('Presentation deleted');
      if (currentPresentation?.id === id) {
        setCurrentPresentation(null);
      }
      fetchPresentations();
    } catch (error) {
      toast.error('Failed to delete presentation');
    }
  };

  const regenerateSlide = async () => {
    if (!currentPresentation) return;
    
    setRegenerating(true);
    try {
      const response = await api.post(
        `/presentations/${currentPresentation.id}/regenerate-slide/${currentSlideIndex}`,
        { instructions: regenerateInstructions || 'Make it better and more engaging' }
      );
      
      // Update the current presentation with the new slide
      const updatedSlides = [...currentPresentation.slides];
      updatedSlides[currentSlideIndex] = response.data.slide;
      setCurrentPresentation({
        ...currentPresentation,
        slides: updatedSlides
      });
      
      toast.success('Slide regenerated!');
      setShowEditDialog(false);
      setRegenerateInstructions('');
    } catch (error) {
      toast.error('Failed to regenerate slide');
    } finally {
      setRegenerating(false);
    }
  };

  const updateSlide = async (slideData) => {
    if (!currentPresentation) return;
    
    try {
      const updatedSlides = [...currentPresentation.slides];
      updatedSlides[currentSlideIndex] = slideData;
      
      await api.put(`/presentations/${currentPresentation.id}`, {
        slides: updatedSlides
      });
      
      setCurrentPresentation({
        ...currentPresentation,
        slides: updatedSlides
      });
      
      toast.success('Slide updated');
      setEditingSlide(null);
    } catch (error) {
      toast.error('Failed to update slide');
    }
  };

  const copyToClipboard = async () => {
    if (!currentPresentation) return;
    
    const text = currentPresentation.slides.map((slide, i) => 
      `Slide ${i + 1}: ${slide.title}\n${slide.content.map(c => `  - ${c}`).join('\n')}\n\nNotes: ${slide.notes || 'None'}\n`
    ).join('\n---\n\n');
    
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const downloadPowerPoint = async () => {
    if (!currentPresentation) return;
    
    try {
      const response = await api.get(`/presentations/${currentPresentation.id}/download-pptx`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${currentPresentation.title.replace(/\s+/g, '_')}.pptx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PowerPoint downloaded!');
    } catch (error) {
      toast.error('Failed to download PowerPoint');
    }
  };

  const generateAllImages = async () => {
    if (!currentPresentation) return;
    
    try {
      toast.info('Generating images for all slides...');
      const response = await api.post(`/presentations/${currentPresentation.id}/generate-images`);
      
      if (response.data.images_generated > 0) {
        toast.success(`Generated ${response.data.images_generated} images!`);
        // Reload the presentation to get updated images
        loadPresentation(currentPresentation.id);
      } else {
        toast.info('No new images needed');
      }
    } catch (error) {
      toast.error('Failed to generate images');
    }
  };

  const exportPresentation = () => {
    if (!currentPresentation) return;
    
    const content = {
      title: currentPresentation.title,
      slides: currentPresentation.slides,
      exportedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(content, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentPresentation.title.replace(/\s+/g, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Card className="card-icrcl">
          <CardContent className="py-16 text-center">
            <Presentation className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">Access Restricted</h2>
            <p className="text-slate-500">AI Presentation Builder is available for Super Admin only.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentSlide = currentPresentation?.slides?.[currentSlideIndex];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f] flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-amber-500" />
            AI Presentation Builder
          </h1>
          <p className="text-slate-500 mt-1">Create professional presentations with AI assistance</p>
        </div>
        
        <Button 
          onClick={() => setShowNewDialog(true)} 
          className="bg-gradient-to-r from-[#1e3a5f] to-[#2a9d8f] hover:opacity-90"
          data-testid="new-presentation-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Presentation
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Saved Presentations Sidebar */}
        <Card className="lg:col-span-1 card-icrcl">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[#1e3a5f]">My Presentations</CardTitle>
            <CardDescription>{presentations.length} saved</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[60vh] overflow-y-auto">
            {presentations.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No presentations yet</p>
            ) : (
              presentations.map((pres) => (
                <div
                  key={pres.id}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${
                    currentPresentation?.id === pres.id 
                      ? 'bg-[#1e3a5f]/10 border-l-4 border-[#1e3a5f]' 
                      : 'hover:bg-slate-50'
                  }`}
                  onClick={() => loadPresentation(pres.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{pres.title}</p>
                      <p className="text-xs text-slate-500">{pres.topic}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">{pres.style}</Badge>
                        <span className="text-xs text-slate-400">
                          {new Date(pres.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={(e) => {
                        e.stopPropagation();
                        deletePresentation(pres.id);
                      }}
                    >
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Main Content Area */}
        <div className="lg:col-span-3 space-y-4">
          {currentPresentation ? (
            <>
              {/* Presentation Header */}
              <Card className="card-icrcl">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-[#1e3a5f]">{currentPresentation.title}</h2>
                      <p className="text-sm text-slate-500">
                        {currentPresentation.slides?.length} slides • {currentPresentation.style} style
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button variant="outline" size="sm" onClick={copyToClipboard}>
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                      <Button variant="outline" size="sm" onClick={generateAllImages} title="Generate AI images for all slides">
                        <Image className="w-4 h-4 mr-1" />
                        Gen Images
                      </Button>
                      <Button variant="outline" size="sm" onClick={downloadPowerPoint} className="bg-orange-50 hover:bg-orange-100 border-orange-200">
                        <Download className="w-4 h-4 mr-1" />
                        PPTX
                      </Button>
                      <Button variant="outline" size="sm" onClick={exportPresentation}>
                        <Download className="w-4 h-4 mr-1" />
                        JSON
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Slide Viewer */}
              <Card className="card-icrcl">
                <CardContent className="p-6">
                  {/* Slide Navigation */}
                  <div className="flex items-center justify-between mb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentSlideIndex(Math.max(0, currentSlideIndex - 1))}
                      disabled={currentSlideIndex === 0}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <span className="text-sm font-medium text-slate-600">
                      Slide {currentSlideIndex + 1} of {currentPresentation.slides?.length}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentSlideIndex(Math.min(currentPresentation.slides?.length - 1, currentSlideIndex + 1))}
                      disabled={currentSlideIndex === currentPresentation.slides?.length - 1}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Slide Content */}
                  {currentSlide && (
                    <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-8 min-h-[400px] text-white shadow-2xl">
                      <div className="flex justify-between items-start mb-6">
                        <h3 className="text-2xl font-bold">{currentSlide.title}</h3>
                        <Badge className="bg-white/20">{currentSlide.layout}</Badge>
                      </div>
                      
                      <div className={`grid ${currentSlide.image_url ? 'grid-cols-2 gap-6' : 'grid-cols-1'}`}>
                        <div>
                          <ul className="space-y-3">
                            {currentSlide.content?.map((point, i) => (
                              <li key={i} className="flex items-start gap-3">
                                <span className="w-2 h-2 mt-2 rounded-full bg-[#2a9d8f] flex-shrink-0" />
                                <span className="text-lg">{point}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        {currentSlide.image_url && (
                          <div className="flex items-center justify-center">
                            <img 
                              src={currentSlide.image_url} 
                              alt={currentSlide.title}
                              className="max-h-[300px] rounded-lg shadow-lg object-cover"
                            />
                          </div>
                        )}
                      </div>

                      {currentSlide.image_prompt && !currentSlide.image_url && (
                        <div className="mt-6 p-4 bg-white/10 rounded-lg">
                          <p className="text-sm text-white/70">
                            <Image className="w-4 h-4 inline mr-2" />
                            Image suggestion: {currentSlide.image_prompt}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Slide Actions */}
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingSlide({ ...currentSlide });
                        }}
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowEditDialog(true)}
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Regenerate with AI
                      </Button>
                    </div>
                    
                    {currentSlide?.notes && (
                      <div className="text-sm text-slate-500">
                        <Clock className="w-4 h-4 inline mr-1" />
                        Notes: {currentSlide.notes.substring(0, 50)}...
                      </div>
                    )}
                  </div>

                  {/* Speaker Notes */}
                  {currentSlide?.notes && (
                    <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                      <h4 className="text-sm font-medium text-slate-700 mb-2">Speaker Notes</h4>
                      <p className="text-sm text-slate-600">{currentSlide.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Slide Thumbnails */}
              <Card className="card-icrcl">
                <CardContent className="py-4">
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {currentPresentation.slides?.map((slide, i) => (
                      <div
                        key={i}
                        className={`flex-shrink-0 w-32 p-2 rounded-lg cursor-pointer transition-all ${
                          i === currentSlideIndex 
                            ? 'bg-[#1e3a5f] text-white' 
                            : 'bg-slate-100 hover:bg-slate-200'
                        }`}
                        onClick={() => setCurrentSlideIndex(i)}
                      >
                        <p className="text-xs font-medium truncate">{i + 1}. {slide.title}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="card-icrcl">
              <CardContent className="py-16 text-center">
                <Presentation className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">No Presentation Selected</h3>
                <p className="text-slate-500 mb-4">Create a new presentation or select one from the list</p>
                <Button onClick={() => setShowNewDialog(true)} className="bg-[#1e3a5f]">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Presentation
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* New Presentation Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Create AI Presentation
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Topic / Title *</Label>
              <Input
                value={newPresentation.topic}
                onChange={(e) => setNewPresentation({ ...newPresentation, topic: e.target.value })}
                placeholder="e.g., Q4 Sales Strategy, Team Building Workshop..."
                data-testid="presentation-topic"
              />
            </div>

            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea
                value={newPresentation.description}
                onChange={(e) => setNewPresentation({ ...newPresentation, description: e.target.value })}
                placeholder="Add more context about what you want to cover..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Number of Slides</Label>
                <Select
                  value={newPresentation.num_slides.toString()}
                  onValueChange={(v) => setNewPresentation({ ...newPresentation, num_slides: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[5, 6, 7, 8, 10, 12, 15].map((n) => (
                      <SelectItem key={n} value={n.toString()}>{n} slides</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Style</Label>
                <Select
                  value={newPresentation.style}
                  onValueChange={(v) => setNewPresentation({ ...newPresentation, style: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STYLE_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Target Audience (Optional)</Label>
              <Input
                value={newPresentation.audience}
                onChange={(e) => setNewPresentation({ ...newPresentation, audience: e.target.value })}
                placeholder="e.g., Executive team, New employees, Clients..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>Cancel</Button>
            <Button 
              onClick={generatePresentation} 
              disabled={generating || !newPresentation.topic.trim()}
              className="bg-gradient-to-r from-[#1e3a5f] to-[#2a9d8f]"
              data-testid="generate-presentation-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate Slide Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate Slide with AI</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              Provide instructions on how you'd like to improve this slide.
            </p>
            <Textarea
              value={regenerateInstructions}
              onChange={(e) => setRegenerateInstructions(e.target.value)}
              placeholder="e.g., Make it more concise, Add statistics, Focus on benefits..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button 
              onClick={regenerateSlide}
              disabled={regenerating}
              className="bg-[#1e3a5f]"
            >
              {regenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Regenerating...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Slide Dialog */}
      {editingSlide && (
        <Dialog open={!!editingSlide} onOpenChange={() => setEditingSlide(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Slide</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={editingSlide.title}
                  onChange={(e) => setEditingSlide({ ...editingSlide, title: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Content (one bullet per line)</Label>
                <Textarea
                  value={editingSlide.content?.join('\n') || ''}
                  onChange={(e) => setEditingSlide({ 
                    ...editingSlide, 
                    content: e.target.value.split('\n').filter(l => l.trim()) 
                  })}
                  rows={5}
                />
              </div>

              <div className="space-y-2">
                <Label>Layout</Label>
                <Select
                  value={editingSlide.layout}
                  onValueChange={(v) => setEditingSlide({ ...editingSlide, layout: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LAYOUT_OPTIONS.map((l) => (
                      <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Speaker Notes</Label>
                <Textarea
                  value={editingSlide.notes || ''}
                  onChange={(e) => setEditingSlide({ ...editingSlide, notes: e.target.value })}
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingSlide(null)}>Cancel</Button>
              <Button onClick={() => updateSlide(editingSlide)} className="bg-[#1e3a5f]">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
